import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import trainingApi from '../../services/trainingApi';

const FIELDS = [
  { key: 'whereYouAre', label: 'Where you are', helper: 'The setting and why the patient is here today, as they would say it' },
  { key: 'recentEvents', label: 'What has happened recently', helper: 'Recent events the patient knows about' },
  { key: 'howYouAreToday', label: 'How you are today', helper: 'Mood, and what they want from this appointment' },
  { key: 'withheldUnlessAskedWell', label: 'What you will not volunteer unless asked well', helper: 'Things they hide or play down until the clinician has earned their trust' },
];

/**
 * "In this scenario" (6 Oct 2026): the patient's situation for this exercise, on top of
 * the persona. The AI playing the patient reads it after the persona's own instructions,
 * so where they differ this wins, and the same persona can serve several scenarios.
 * The clinician's background brief is never passed to the patient; "Draft from the
 * background brief" rewrites its facts from the patient's side for the lecturer to review.
 */
const PatientSituationPanel = ({ value = {}, onChange, scenario, readOnly }) => {
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState(null);
  const ctx = scenario.scenarioContext || {};
  const anyFilled = FIELDS.some(f => (value[f.key] || '').trim());

  const handleDraft = async () => {
    setDrafting(true);
    setError(null);
    try {
      const personaId = typeof scenario.clientPersonaId === 'object' ? scenario.clientPersonaId?._id : scenario.clientPersonaId;
      const response = await trainingApi.draftPatientSituation({
        backgroundBrief: ctx.backgroundBrief,
        presentingComplaint: ctx.presentingComplaint,
        setting: ctx.setting,
        referralSource: ctx.referralSource,
        personaId,
      });
      const body = response.data || response;
      const draft = body.data || body;
      // Only fill what is empty, so nothing the lecturer wrote is overwritten.
      const next = { ...value };
      FIELDS.forEach(f => { if (!(next[f.key] || '').trim() && draft[f.key]) next[f.key] = draft[f.key]; });
      onChange(next);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not draft this. Try again, or fill the fields in yourself.');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>In this scenario</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The patient&apos;s situation for this exercise. The AI playing the patient reads this after the persona, so where they
        differ, this is what is true today. Write it from the patient&apos;s side. Leave it empty to use the persona as it is.
      </Typography>
      {!readOnly && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={drafting ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
            onClick={handleDraft}
            disabled={drafting || !(ctx.backgroundBrief || '').trim()}
          >
            {drafting ? 'Drafting...' : 'Draft from the background brief'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {(ctx.backgroundBrief || '').trim()
              ? 'Fills empty fields only. Check the wording before saving: the brief itself is never shown to the patient.'
              : 'Add a background brief above to use this.'}
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {FIELDS.map(f => (
          <TextField
            key={f.key}
            id={`patient-situation-${f.key}`}
            label={f.label}
            helperText={f.helper}
            value={value[f.key] || ''}
            onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            disabled={readOnly}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 1500 }}
          />
        ))}
      </Box>
      {anyFilled && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Run a Preview after saving to read how the patient comes across.
        </Typography>
      )}
    </Box>
  );
};

export default PatientSituationPanel;
